import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Calendar, CalendarClock, Sunrise } from 'lucide-react-native';
import { useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { HoneyCTA, OptionRow, SkipLink } from '@/components/onboarding/Controls';
import { Eyebrow, H1, H1Em, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import { now } from '@/db/ids';
import type { NotificationFrequency } from '@/db/types';
import {
  composeAndScheduleDigest,
  dayLabel,
  requestNotificationPermissionUnified,
  schedulePostOnboardingNudge,
} from '@/lib/notifications';

/**
 * Onboarding cadence step — asked right after the plan commits, before
 * the user lands in the app. "When should we check in?" is the natural
 * lead-in to the OS permission dialog: the user has just told us the
 * rhythm they want, so the permission ask arrives with full context
 * (best-practice "prime then prompt").
 *
 * The chosen cadence writes to the same property columns the settings
 * page edits (notification_frequency / _day / _hour), so Settings →
 * Notifications shows and edits the same preference.
 *
 * Skip path: keeps the DB defaults (weekly, Monday, 8 AM), does NOT
 * pop the permission dialog (the user just declined to engage — asking
 * anyway would be the nagging we promise not to do), and still lands
 * in the app. The post-onboarding "tune your plan" nudge schedules
 * only if permission already happens to be granted.
 */

const HOURS = [7, 8, 9, 10, 12, 17, 18, 19, 20];

type Cadence = Exclude<NotificationFrequency, 'off'>;

const OPTIONS: {
  v: Cadence;
  title: string;
  sub: string;
  badge?: string;
  Icon: typeof Calendar;
}[] = [
  {
    v: 'weekly',
    title: 'Once a week',
    sub: 'A single digest with your next task, on the day you pick',
    badge: 'Recommended',
    Icon: Calendar,
  },
  {
    v: 'daily',
    title: 'Every day',
    sub: "Today's one thing, each morning",
    Icon: Sunrise,
  },
  {
    v: 'monthly',
    title: 'Once a month',
    sub: 'One gentle check-in on the 1st',
    Icon: CalendarClock,
  },
];

export default function NotifyScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const { propertyId } = useLocalSearchParams<{ propertyId?: string }>();

  const [freq, setFreq] = useState<Cadence>('weekly');
  // Saturday morning — when most homeowners actually do house things.
  const [weekday, setWeekday] = useState(7); // Expo convention: 1 = Sun … 7 = Sat
  const [hour, setHour] = useState(9);
  const [saving, setSaving] = useState(false);

  const enterApp = () => router.replace('/(tabs)' as never);

  const onSave = async () => {
    setSaving(true);
    try {
      if (propertyId) {
        await db.runAsync(
          `UPDATE property SET
             notifications_enabled = 1,
             notification_frequency = ?,
             notification_day = ?,
             notification_hour = ?,
             updated_at = ?
           WHERE id = ?`,
          freq,
          weekday - 1, // DB stores 0-6 (0 = Sun)
          hour,
          now(),
          propertyId
        );
        const status = await requestNotificationPermissionUnified();
        if (status === 'granted') {
          await composeAndScheduleDigest(db);
          void schedulePostOnboardingNudge(propertyId, { promptIfNeeded: false });
        }
      }
    } catch (err) {
      // Preference save failures shouldn't strand the user at the end
      // of onboarding — the same controls live in Settings.
      console.warn('cadence save failed', err);
    }
    enterApp();
  };

  const onSkip = () => {
    if (propertyId) {
      void schedulePostOnboardingNudge(propertyId, { promptIfNeeded: false });
    }
    enterApp();
  };

  return (
    <Screen
      scroll
      footer={
        <>
          <HoneyCTA label="Set my rhythm" onPress={onSave} loading={saving} />
          <SkipLink label="I'll decide later — Settings has this too" onPress={onSkip} />
        </>
      }>
      <Eyebrow>One last thing</Eyebrow>
      <H1>
        When should we{'\n'}
        <H1Em>check in?</H1Em>
      </H1>
      <WhyLine>
        One gentle digest with your next task — never a pile of pings.
        Pick the rhythm that fits your life.
      </WhyLine>

      <View style={styles.options}>
        {OPTIONS.map((o) => (
          <OptionRow
            key={o.v}
            title={o.title}
            subtitle={o.sub}
            badge={o.badge}
            selected={freq === o.v}
            icon={
              <o.Icon
                size={20}
                color={freq === o.v ? Palette.cream : Palette.forest}
                strokeWidth={1.9}
              />
            }
            onPress={() => setFreq(o.v)}
          />
        ))}
      </View>

      {freq === 'weekly' ? (
        <>
          <Text style={styles.pickerLabel}>WHICH DAY</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {[1, 2, 3, 4, 5, 6, 7].map((d) => {
              const sel = weekday === d;
              return (
                <Pressable
                  key={d}
                  onPress={() => setWeekday(d)}
                  style={({ pressed }) => [
                    styles.chip,
                    sel && styles.chipSel,
                    pressed && { opacity: 0.9 },
                  ]}>
                  <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                    {dayLabel(d)}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </>
      ) : null}

      <Text style={styles.pickerLabel}>WHAT TIME</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {HOURS.map((h) => {
          const sel = hour === h;
          return (
            <Pressable
              key={h}
              onPress={() => setHour(h)}
              style={({ pressed }) => [
                styles.chip,
                sel && styles.chipSel,
                pressed && { opacity: 0.9 },
              ]}>
              <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                {formatHour(h)}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      {freq === 'monthly' ? (
        <Text style={styles.hint}>
          We&apos;ll check in on the 1st of each month at {formatHour(hour)}.
        </Text>
      ) : null}

      {Platform.OS === 'web' ? (
        <Text style={styles.hint}>
          Scheduled digests arrive on the iOS / Android app. On web,
          Homestead shows what&apos;s due whenever you open it.
        </Text>
      ) : null}
    </Screen>
  );
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

const styles = StyleSheet.create({
  options: {
    marginTop: Spacing.four,
    gap: Spacing.two,
  },
  pickerLabel: {
    marginTop: Spacing.four,
    marginBottom: 8,
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.7,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    marginRight: 6,
  },
  chipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  chipTextSel: { color: Palette.cream },
  hint: {
    marginTop: Spacing.three,
    fontSize: 13,
    lineHeight: 17,
    fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft,
  },
});
